/**
 * Metrics Card
 *
 * Displays all extracted quantitative metrics with confidence indicators,
 * units, and source attribution. Groups by confidence tier for scanability.
 */

import React, { useMemo, useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { BarChart3, ChevronDown, TrendingUp } from 'lucide-react-native';
import Animated, { FadeIn, FadeInRight } from 'react-native-reanimated';
import type { RichExtractionMetric } from '@/lib/supabase/edge-functions';
import { useThemeColors } from '@/lib/useThemeColors';

interface MetricsCardProps {
  metrics: RichExtractionMetric[];
}

function formatValue(value: number, unit: string): string {
  const formatted = value >= 1_000_000_000
    ? `${(value / 1_000_000_000).toFixed(1)}B`
    : value >= 1_000_000
    ? `${(value / 1_000_000).toFixed(1)}M`
    : value >= 1_000
    ? `${(value / 1_000).toFixed(1)}K`
    : value.toLocaleString();

  if (unit === '$' || unit === '€' || unit === '£') return `${unit}${formatted}`;
  if (unit === '%') return `${formatted}%`;
  return unit ? `${formatted} ${unit}` : formatted;
}

export function MetricsCard({ metrics }: MetricsCardProps) {
  const colors = useThemeColors();
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(() =>
    [...metrics].sort((a, b) => b.confidence - a.confidence),
    [metrics],
  );

  const highConf = sorted.filter(m => m.confidence >= 70);
  const medConf = sorted.filter(m => m.confidence >= 40 && m.confidence < 70);
  const lowConf = sorted.filter(m => m.confidence < 40);

  const visibleMetrics = expanded ? sorted : sorted.slice(0, 8);
  const hasMore = sorted.length > 8;

  return (
    <Animated.View entering={FadeIn.delay(150).duration(400)}>
      <View
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {/* Header */}
        <View className="p-4 pb-3">
          <View className="flex-row items-center gap-2.5 mb-2">
            <View
              className="w-8 h-8 rounded-lg items-center justify-center"
              style={{ backgroundColor: '#EEF2FF' }}
            >
              <BarChart3 size={16} color="#6366F1" />
            </View>
            <View className="flex-1">
              <Text style={{ color: colors.text.primary }} className="text-sm font-bold">
                Extracted Metrics
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                {metrics.length} data points recovered
              </Text>
            </View>
          </View>

          {/* Confidence tier summary */}
          <View className="flex-row gap-3">
            {highConf.length > 0 && (
              <View className="flex-row items-center gap-1">
                <View className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.success }} />
                <Text style={{ color: colors.success }} className="text-xs font-semibold">{highConf.length}</Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">high</Text>
              </View>
            )}
            {medConf.length > 0 && (
              <View className="flex-row items-center gap-1">
                <View className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.warning }} />
                <Text style={{ color: colors.warning }} className="text-xs font-semibold">{medConf.length}</Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">medium</Text>
              </View>
            )}
            {lowConf.length > 0 && (
              <View className="flex-row items-center gap-1">
                <View className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.error }} />
                <Text style={{ color: colors.error }} className="text-xs font-semibold">{lowConf.length}</Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">low</Text>
              </View>
            )}
          </View>
        </View>

        {/* Metric rows */}
        <View style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}>
          {visibleMetrics.map((m, i) => {
            const confColor = m.confidence >= 70 ? colors.success : m.confidence >= 40 ? colors.warning : colors.error;
            const confBg = m.confidence >= 70 ? colors.traffic.greenLight : m.confidence >= 40 ? colors.amber.light : colors.red.light;

            return (
              <Animated.View
                key={`${m.name}-${i}`}
                entering={FadeInRight.delay(i * 25).duration(200)}
                className="flex-row items-center px-4 py-2.5 gap-3"
                style={i < visibleMetrics.length - 1 ? { borderBottomWidth: 0.5, borderBottomColor: colors.border.light } : undefined}
              >
                {/* Confidence dot */}
                <View className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: confColor }} />

                {/* Name + source */}
                <View className="flex-1">
                  <Text style={{ color: colors.text.primary }} className="text-xs font-semibold">
                    {m.name}
                  </Text>
                  {m.source ? (
                    <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                      {m.source}
                    </Text>
                  ) : null}
                </View>

                {/* Value */}
                <Text style={{ color: colors.text.primary }} className="text-sm font-bold">
                  {formatValue(m.value, m.unit)}
                </Text>

                {/* Confidence badge */}
                <View className="px-1.5 py-0.5 rounded-md min-w-[36]  items-center" style={{ backgroundColor: confBg }}>
                  <Text style={{ color: confColor }} className="text-xs font-bold">
                    {m.confidence}%
                  </Text>
                </View>
              </Animated.View>
            );
          })}

          {hasMore && (
            <Pressable
              onPress={() => setExpanded(!expanded)}
              className="flex-row items-center justify-center py-2.5 gap-1"
              style={{ borderTopWidth: 0.5, borderTopColor: colors.border.light }}
            >
              <Text style={{ color: colors.primary[600] }} className="text-xs font-semibold">
                {expanded ? 'Show less' : `Show all ${sorted.length} metrics`}
              </Text>
              <ChevronDown
                size={12}
                color={colors.primary[600]}
                style={{ transform: [{ rotate: expanded ? '180deg' : '0deg' }] }}
              />
            </Pressable>
          )}
        </View>
      </View>
    </Animated.View>
  );
}
